import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { JobRequestsService } from '../../../services/job-requests.service';
import { AttachmentService } from '../../../services/attachment.service';
import { JobRequestShippingInformationDto, ShipmentRecordDto } from '@models/shipping-information-dto.interface';

export interface ShippingInformationDialogData {
  jobRequestId: number;
  jobRequestName: string;
}

@Component({
  selector: 'app-shipping-information-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './shipping-information-dialog.component.html',
  styleUrl: './shipping-information-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShippingInformationDialogComponent implements OnInit {
  private readonly dialogRef = inject(MatDialogRef<ShippingInformationDialogComponent, boolean>);
  protected readonly data = inject<ShippingInformationDialogData>(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly jobRequestsService = inject(JobRequestsService);
  private readonly attachmentService = inject(AttachmentService);

  private static readonly cementLabDestination = 'Cement Lab';

  protected readonly isLoading = signal(true);
  protected readonly isSaving = signal(false);
  protected readonly isDownloadingForm = signal(false);
  protected readonly errorMessage = signal('');

  protected readonly form = this.fb.group({
    isShippedOutside: [false],
    isShippedToCement: [false],
    outsideVolume: [null as number | null],
    outsideShippedOn: [''],
    outsideDestination: [''],
    cementVolume: [null as number | null],
    cementShippedOn: [''],
  });

  ngOnInit(): void {
    this.form.controls.isShippedOutside.valueChanges.subscribe(() => this.applySectionValidation());
    this.form.controls.isShippedToCement.valueChanges.subscribe(() => this.applySectionValidation());

    this.jobRequestsService.getShippingInformation(this.data.jobRequestId).subscribe({
      next: (dto) => {
        this.applyShippingInformation(dto);
        this.isLoading.set(false);
      },
      error: () => {
        this.errorMessage.set('Unable to load shipping information.');
        this.isLoading.set(false);
      },
    });
  }

  protected onSave(): void {
    this.applySectionValidation();
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    const raw = this.form.getRawValue();
    const records: ShipmentRecordDto[] = [];

    if (raw.isShippedOutside) {
      records.push({
        destination: (raw.outsideDestination ?? '').trim() || null,
        volume: Number(raw.outsideVolume ?? 0),
        shippedOn: this.toIsoDate(raw.outsideShippedOn),
        carrier: null,
        trackingNumber: null,
      });
    }

    if (raw.isShippedToCement) {
      records.push({
        destination: ShippingInformationDialogComponent.cementLabDestination,
        volume: Number(raw.cementVolume ?? 0),
        shippedOn: this.toIsoDate(raw.cementShippedOn),
        carrier: null,
        trackingNumber: null,
      });
    }

    this.isSaving.set(true);
    this.errorMessage.set('');

    this.jobRequestsService.updateShippingInformation(this.data.jobRequestId, records).subscribe({
      next: () => {
        this.isSaving.set(false);
        this.dialogRef.close(true);
      },
      error: () => {
        this.isSaving.set(false);
        this.errorMessage.set('Unable to save shipping information.');
      },
    });
  }

  protected onCancel(): void {
    this.dialogRef.close(false);
  }

  protected onDownloadShippingForm(): void {
    if (this.isDownloadingForm()) return;

    this.isDownloadingForm.set(true);
    this.errorMessage.set('');

    this.attachmentService.downloadByPath('attachments/shipping-form').subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = 'shipping_form.docx';

        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);

        URL.revokeObjectURL(url);
        this.isDownloadingForm.set(false);
      },
      error: () => {
        this.isDownloadingForm.set(false);
        this.errorMessage.set('Unable to download shipping form.');
      },
    });
  }

  protected showOutsideDestinationError(): boolean {
    const control = this.form.controls.outsideDestination;
    return control.touched && control.hasError('required');
  }

  protected showOutsideVolumeError(): boolean {
    const control = this.form.controls.outsideVolume;
    return control.touched && control.hasError('required');
  }

  protected showCementVolumeError(): boolean {
    const control = this.form.controls.cementVolume;
    return control.touched && control.hasError('required');
  }

  private applyShippingInformation(dto: JobRequestShippingInformationDto): void {
    const records = dto.shipmentRecords ?? [];
    const cementRecord = records.find(record =>
      (record.destination ?? '').trim().toLowerCase() === ShippingInformationDialogComponent.cementLabDestination.toLowerCase());
    const outsideRecord = records.find(record =>
      (record.destination ?? '').trim().toLowerCase() !== ShippingInformationDialogComponent.cementLabDestination.toLowerCase());

    const today = this.getTodayDateString();

    this.form.patchValue({
      isShippedOutside: !!outsideRecord,
      isShippedToCement: !!cementRecord,
      outsideVolume: outsideRecord?.volume ?? dto.shippingVolume,
      outsideShippedOn: this.toDateInput(outsideRecord?.shippedOn) ?? today,
      outsideDestination: outsideRecord?.destination ?? '',
      cementVolume: cementRecord?.volume ?? dto.cementLabVolume,
      cementShippedOn: this.toDateInput(cementRecord?.shippedOn) ?? today,
    });

    this.applySectionValidation();
  }

  private applySectionValidation(): void {
    const outsideEnabled = !!this.form.controls.isShippedOutside.value;
    const cementEnabled = !!this.form.controls.isShippedToCement.value;

    if (outsideEnabled) {
      this.form.controls.outsideVolume.setValidators([Validators.required]);
      this.form.controls.outsideDestination.setValidators([Validators.required]);
    } else {
      this.form.controls.outsideVolume.clearValidators();
      this.form.controls.outsideDestination.clearValidators();
    }

    if (cementEnabled) {
      this.form.controls.cementVolume.setValidators([Validators.required]);
    } else {
      this.form.controls.cementVolume.clearValidators();
    }

    this.form.controls.outsideVolume.updateValueAndValidity({ emitEvent: false });
    this.form.controls.outsideDestination.updateValueAndValidity({ emitEvent: false });
    this.form.controls.cementVolume.updateValueAndValidity({ emitEvent: false });
  }

  private toDateInput(value: string | null | undefined): string | null {
    if (!value) return null;

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private toIsoDate(value: string | null | undefined): string | null {
    if (!value) return null;

    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
  }

  private getTodayDateString(): string {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
